import React, { useState } from 'react';

import { Button, TooltipPortal } from '~components';
import Icon, { faCopy } from '~components/icon';
import PopOver from '~components/pop-over/PopOver';

import { formatDexCopyPreview } from '~dex/lib/dexCopyPreview';

interface ComponentProps {
  /** Unique within the row - used for the popover target id and the data-cy hooks. */
  id: string;
  /** The originating repeat appointment's own reference number (e.g. "00000551"), for naming it
   * the same way the report's own "Reference" column does. Falls back to the raw id if a parent
   * genuinely has no reference number recorded. */
  parentReference: string | number;
  preview: Record<string, string | number | boolean | null>;
  onConfirm: () => Promise<void>;
  isLoading?: boolean;
}

/**
 * DexCopyPreviewPopover
 *
 * Click-opened popover on the DEX Readiness Report's copy action, showing exactly which fields a
 * copy would write - and their values - before it happens (TAV-3133 follow-up, Beth Kairys review
 * feedback: "someone who is about to copy a field over would want to check what they are about to
 * copy in"). copy_preview is computed server-side via the same field-filtering the copy endpoint
 * itself applies at write time, so this can never show something other than what actually gets
 * written.
 *
 * Built on the same PopOver primitive as the app's existing ClaimItemHoverPopover
 * (candidate/TAV-2599): a coloured popover-header over a plain popover-body. That component is
 * hover-opened and read-only; this one holds focusable Cancel/Copy controls, so it opens on click
 * and uses PopOver's isModal (real dialog semantics, not the hover-only tooltip role) rather than
 * copying its hover/touch handling verbatim. The header's gradient matches the app's standard
 * modal header (e.g. Support at Home > New Claim), and Cancel/Copy use the same
 * secondary/success colours that modal's own footer buttons do.
 */
const DexCopyPreviewPopover: React.FC<ComponentProps> = ({ id, parentReference, preview, onConfirm, isLoading }) => {
  const [isOpen, setIsOpen] = useState(false);
  const targetId = `dex-copy-preview-${id}`;
  const bodyId = `dex-copy-preview-body-${id}`;
  const entries = formatDexCopyPreview(preview);

  const close = () => setIsOpen(false);
  const toggle = () => setIsOpen((prev) => !prev);

  const handleConfirm = async () => {
    await onConfirm();
    close();
  };

  return (
    <>
      {/* Not a plain <button> - matching ClickableIcon's own div/role="button" markup (same
          clickable-icon/clickable-small/icon-round classes, manual Enter/Space handling) is what
          gives this trigger the same hover circle and spacing as the row's other icons. */}
      <div
        role="button"
        tabIndex={0}
        id={targetId}
        className="clickable-icon clickable-small icon-round"
        aria-label="Preview and copy DEX data"
        aria-expanded={isOpen}
        aria-controls={isOpen ? bodyId : undefined}
        onClick={toggle}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggle();
          }
        }}
        data-cy={`dex-readiness-copy-${id}`}
        style={{ userSelect: 'none' }}
      >
        <Icon icon={faCopy} id="icon-small" className="icon-small text-black" />
      </div>
      {/* left, not the default top: this trigger sits in the table's rightmost column, so a
          horizontally-centered tooltip has to shift hard left to stay on-screen (see
          TooltipPortal's viewport clamp), leaving its arrow visually adrift from the box. Matches
          ClaimItemHoverPopover's own default for the same rightmost-column reasoning. */}
      {!isOpen && (
        <TooltipPortal
          targetId={targetId}
          content="Copy DEX data from originating repeat appointment"
          placement="left"
        />
      )}
      <PopOver
        isOpen={isOpen}
        target={targetId}
        placement="left"
        autoFlip
        toggle={close}
        ariaLabel="Copy DEX data"
        className="popover show bs-popover-auto popover-inner"
        styleOverrides={{ padding: 0, width: 300 }}
      >
        <h3 className="popover-header dex-copy-preview-header mb-0">Copy DEX Data</h3>
        <div id={bodyId} className="popover-body text-start">
          <p className="text-muted small mb-2">
            Copy from originating appointment Ref #<strong>{parentReference}</strong>
          </p>
          {entries.length === 0 ? (
            <p className="mb-3" data-cy={`dex-readiness-copy-${id}-no-fields`}>
              No applicable fields to copy.
            </p>
          ) : (
            <ul className="list-unstyled mb-3" data-cy={`dex-readiness-copy-${id}-fields`}>
              {entries.map((entry) => (
                <li key={entry.column} className="d-flex justify-content-between gap-3">
                  <span className="text-muted">{entry.label}</span>
                  <strong>{entry.value}</strong>
                </li>
              ))}
            </ul>
          )}
          <div className="d-flex justify-content-end gap-2">
            <Button color="secondary" size="sm" onClick={close} dataCy={`dex-readiness-copy-${id}-cancel`}>
              Cancel
            </Button>
            <Button
              color="success"
              size="sm"
              onClick={handleConfirm}
              loading={isLoading}
              disabled={entries.length === 0}
              dataCy={`dex-readiness-copy-${id}-confirm`}
            >
              Copy
            </Button>
          </div>
        </div>
      </PopOver>
    </>
  );
};

export default DexCopyPreviewPopover;
